/* @pareto-engineering/generator-front 1.0.12 */
import React, {
  useMemo,
} from 'react'

import {
  AnimatedGradient,
  Page,
  useApp,
  Conversation,
} from '@pareto-engineering/design-system'

import {
  TestimonialCard,
  Steps,
  FAQ,
  Milestone,
  Footer,
  Customers,
  ConversionPage,
  NavigationBar,
  SectionHero,
  SectionCTA,
  commonSectionMap,
  ConversionFormButton,
} from 'modules/components'

import {
  customerLogos,
} from 'modules/common/content'

import MODULE_URLS from '../urls'

const conversation = [
  [
    {
      from   :'ours',
      sender :'Arianne',
      message:'Hi Morgan, excited to manage your work at Pareto. Send me your task instructions to get started!',
    },
    {
      from   :'theirs',
      sender :'Morgan',
      message:'Hi Arianne, we\'d like to build a list of 100 leads. Let\'s use the instructions from our last project.',
    },
    {
      from   :'ours',
      sender :'Arianne',
      message:'Great. To confirm, we\'ll look for U.S. based CEOs or Founders who raised VC in the past year, with teams of 11 to 50 people in the MedTech Industry. We\'ll collect their name, role, company name, last funding date, number of team members, email address, LinkedIn URL.',
    },
  ],
  [
    {
      from   :'theirs',
      sender :'Morgan',
      message:'Sounds great! Just revise to focus on teams of 51 to 100.',
    },
    {
      from   :'ours',
      sender :'Arianne',
      message:'Got it. We estimate 5 to 10 minutes per lead or about 12.5 hours to build a list of 100 leads. Does 12.5 hours sound like a good starting budget? We can target Friday with results.',
    },
    {
      from   :'theirs',
      sender :'Morgan',
      message:'Sounds great. Let\'s get started!',
    },
    {
      from   :'ours',
      sender :'Arianne',
      message:'Awesome. I\'ll have initial results in 24 hours!',
    },
  ],
]

const steps = [
  {
    title      :'Describe your project',
    description:'Our 24/7 team checks your instructions, asks questions to clarify details, and identifies blockers to save you time on costly iterations.',
  },
  {
    title      :'Start tasks in 24 hours',
    description:'Review our cost estimate, set your hourly budget, and see initial results same-day as we match your tasks to experts in our vetted network.',
  },
  {
    title      :'Fully managed & quality assured',
    description:'We guarantee high-quality work with expert review and quality assurance across every project. Enjoy peace of mind at unbeatable prices.',
  },
]

const testimonialsMap = [
  {
    testimonial:'I wish everyone knew how fun it is to work here! It\'s rare to find a company that focuses on the people behind it, not just building a business for profit. We take care of people, we listen to them, and we find ways to make our operations team feel valued.',
    name       :'Arianne',
    role       :'Project Manager',
    image      :'/testimonials/arianne.webp',
  },
  {
    testimonial:'At Pareto, working moms are not confined to the “mom box”. We have the opportunity to keep growing. We feel valued, our voices are heard, and our skills are not overlooked. I am really proud to be a part of Pareto because of that.',
    name       :'May',
    role       :'Quality Assurance Lead',
    image      :'/testimonials/may.webp',
  },
  {
    testimonial:'Having the flexibility to work anywhere, anytime, and earn extra income while still having enough time to look after my kids and manage my small business is incredible.',
    name       :'Lanie',
    role       :'Project Manager',
    image      :'/testimonials/lanie.webp',
  },
]

const HowItWorks = () => {
  const { SITE } = useApp()

  const faqMap = useMemo(() => ([
    {
      question:'What is the Pareto principle?',
      answer  :'The Pareto Principle states that 80% of the results come from 20% of the work. Our purpose at Pareto is to help you focus on the 20% of work that makes 80% of the impact.',
    },
    {
      question:'How long does it take to get set up with Pareto?',
      answer  :(
        <>
          Our team can have you up and running with Pareto in as little as 24 hours.
          Interested in getting started?
          {' '}
          <ConversionFormButton isSimple>
            Speak with our team!
          </ConversionFormButton>
        </>
      ),
    },
    {
      question:'What types of tasks and projects can Pareto assist with?',
      answer  :'Pareto can help you with a wide range of data-heavy, manual tasks and processes across web research, data collection, and lead generation. Including building lists of customers, screening candidates, enriching sales leads, migrating datasets, and much more. Simply let us know your goals, and we\'ll tailor our AI-optimized workflows to your unique needs.',
    },
    {
      question:'Can I use Pareto for a one-time project, or do I need to commit to a long-term contract?',
      answer  :'With monthly plan starting at $20 per hour for 20 hours of work. Send tasks with no minimums and pay as you go for additional hours. We can ramp up or down on-demand to support your needs, big or small.',
    },
    {
      question:'Does Pareto offer any post-project support?',
      answer  :'Yes. Your project manager is available to address any questions or concerns you may have after the completion of your project. If any results don\'t match your project criteria, let us know within 5 days of delivery, and we\'ll redo the work or refund your hours.',
    },
    {
      question:'Can Pareto help with international projects outside the United States?',
      answer  :'Absolutely. We work with companies from around the world. We have experience in working on international projects and can adapt to different time zones and team requirements.',
    },
    {
      question:'How experienced is the team at Pareto?',
      answer  :'Our team consists of college-educated professionals with expertise in data processing and quality assurance across dozens of industries. We have extensive experience working with hundreds of companies across thousands of custom projects. You can trust us to provide insights based on proven workflows.',
    },
    {
      question:'Is Pareto quality guaranteed?',
      answer  :'If any results don\'t match your project criteria, let us know within 5 days of delivery and we\'ll redo the work or refund your hours.',
    },
    {
      question:'What\'s the best way to scope my project?',
      answer  :<>
        Use the simple template below to help us understand your new project.
        Additionally, be sure to share any documents, spreadsheets, or
        resources we should review.
        <ul>
          <li>
            <strong>What</strong>
            : Describe the work you need to get done
          </li>
          <li>
            <strong>Why</strong>
            : Help us understand your business goal
          </li>
          <li>
            <strong>How</strong>
            : Add any instructions you&apos;d like us to follow
          </li>
          <li>
            <strong>Deliverable</strong>
            : Share an example of what success looks like
          </li>
        </ul>
        {/* eslint-disable-next-line react/jsx-closing-tag-location */}
      </>,
    },
    {
      question:'Does Pareto work with my preferred tools and platforms?',
      answer  :'Absolutely. We\'re happy to use any tools or platforms you provide.',
    },
    {
      question:'How much will my project cost?',
      answer  :'Every project is billed hourly based on the time spent training, working on the tasks, and reviewing results for quality assurance. Set an hourly budget, or let us share a time estimate for your project before starting work. We charge $20 per hour for setup, training, QA, and execution under our Starter plan.',
    },
    {
      question:'How do I control my spending?',
      answer  :'Easily control costs by setting a time cap for each project batch and pausing whenever needed. Increase the cap for more weekly results, or opt for priority delivery to receive results within 1-2 days for urgent projects.',
    },
    {
      question:'How does Pareto ensure data privacy and confidentiality?',
      answer  :<>
        Our team takes confidentiality very seriously.
        We abide by the following measures to ensure the highest level of security for our users:
        <ul>
          <li>
            NDAs upon request
          </li>
          <li>
            VPN access protocols
          </li>
          <li>
            Encrypted hard drives
          </li>
          <li>
            Encrypted sharing of sensitive data
          </li>
          <li>
            Background checks for all new hires
          </li>
        </ul>
        {/* eslint-disable-next-line react/jsx-closing-tag-location */}
      </>,
    },
    {
      question:'What should I do if I have other questions?',
      answer  :<>
        Send a message to your project manager or reach us at
        {' '}
        <a href={`mailto:${SITE.SUPPORT_EMAIL}`}>{SITE.SUPPORT_EMAIL}</a>
        {/* eslint-disable-next-line react/jsx-closing-tag-location */}
      </>,
    },
  ]), [])

  const helmetProps = useMemo(() => ({
    title           :'How It Works | Pareto',
    meta_description:'Learn how to multiply your impact in 3 simple steps with our team of superhuman assistants.',
    canonical       :MODULE_URLS.HOW_IT_WORKS,
    og_type         :'website',
    main_image      :`${SITE.CANONICAL}/misc/social-share.jpg`,
    robots          :'noindex',
  }), [])

  return (
    <ConversionPage
      id="how-it-works"
      mobileButtonText="Get started"
      desktopLaunchText="Send task instructions and get work done today."
      mobileLaunchText="Ready to complete your tasks?"
      itemScope
      itemType="https://schema.org/FAQPage"
      helmetProps={helmetProps}
      Background={AnimatedGradient}
      conversionFormConfig={commonSectionMap}
    >
      <NavigationBar />
      <SectionHero
        heading="How it works"
        subtitle="Multiply your impact in 3 simple steps with our team of superhuman assistants."
        className="to-plain"
      />

      <Page.Section
        id="customers"
        className="x-background-far b-x"
        isSpaced
      >
        <Customers
          className="span-8 md-start-2 md-span-12 x-highlighted"
          items={customerLogos}
        />
      </Page.Section>

      <Page.Section
        id="steps"
        className="x-background-far b-x dense"
      >
        <div
          className="grid span-8 md-start-2 md-span-6 dense"
        >
          <p className="span-8 md-span-5 h1 remove-top-margin">
            <span className="x-highlighted c-x">
              How it works
            </span>
          </p>
          <Steps
            items={steps}
            className="span-8 md-span-5"
          />
        </div>
        <Conversation
          className="md-start-9 md-span-5 span-8"
        >
          {
            conversation[0].map((message) => (
              <Conversation.Message
                {...message}
              />
            ))
          }
        </Conversation>
        <Conversation
          className="md-start-9 md-span-5 span-8"
        >
          {
            conversation[1].map((message) => (
              <Conversation.Message
                {...message}
              />
            ))
          }
        </Conversation>
        <div className="md-start-2 md-span-5 span-8">
          <p className="x-paragraph c-x">
            Don&apos;t forget, our work is quality guaranteed.
            If you&apos;re not happy with results, we&apos;ll make it right or refund your hours.
            It&apos;s that simple.
          </p>
          <div className="md-start-2">
            <ConversionFormButton
              color="interactive"
              isGradient
            >
              Start now
            </ConversionFormButton>
          </div>
        </div>
      </Page.Section>

      <Page.Section
        id="profile-milestones"
        className="x-background-far b-x"
        isSpaced
      >
        <p className="span-8 md-start-2 md-span-6 s2">
          Since 2020, we&apos;ve helped businesses across thousands of data operations projects.
          From record breaking sales to game changing process improvements,
          check out the places you&apos;ll go with our team!
        </p>
        <Milestone
          className="span-8 md-start-2 md-span-5 s1"
          company="Stackmatix"
          imagePath="/misc/stackmatix.png"
          achievements={[
            'Achieved 6x increase in revenue',
            '1,260 hours and $66,000 saved',
          ]}
        />
        <Milestone
          className="span-8 md-start-8 md-span-5 s1"
          company="Beaming Health"
          imagePath="/misc/beaming-health.png"
          achievements={[
            'Covered all 50 states in their directory',
            '2,700 hours and $115,000 saved',
          ]}
        />
        <Milestone
          className="span-8 md-start-2 md-span-5 s1"
          company="The Comparison Company"
          imagePath="/misc/the-comparison-company.png"
          achievements={[
            'Launched 2 months ahead of schedule',
            '133 hours and $5,300 saved',
          ]}
        />
        <Milestone
          className="span-8 md-start-8 md-span-5 s1"
          company="Shelflife"
          imagePath="/misc/shelflife.png"
          achievements={[
            'Listed 10,000 products in their supplier directory',
            '665 hours and $25,000 saved',
          ]}
        />
      </Page.Section>

      <Page.Section
        id="profile-testimonials"
        className="from-plain"
      >
        <p className="h1 md-start-2 md-span-8 span-8 remove-bottom-margin">
          <span className="x-highlighted c-x">
            Built by and for underrepresented talent
          </span>
        </p>
        <p className="span-8 md-start-2 md-span-6 s2">
          The infrastructure behind virtual assistance is outdated.
          We&apos;ve joined forces with seasoned experts and researchers to build
          a top-tier community of expert-vetted,
          {' '}
          <span className="x-highlighted c-x">
            diverse professionals around the world.
          </span>
        </p>
        <p className="span-8 md-start-2 md-span-6 s2 remove-top-margin">
          Pareto operates on the principles of equitable compensation, collaboration,
          and supportive management. Our mission is to empower women and overlooked
          talent to build careers in technology.
        </p>
        <TestimonialCard.Group
          className="span-8 md-start-2 md-span-12"
          type="vertical-flexbox"
          desktopType="grid"
        >
          {testimonialsMap.map((testimonial) => (
            <TestimonialCard
              className="md-span-4 s1"
              testimonial={testimonial.testimonial}
              name={testimonial.name}
              role={testimonial.role}
              image={testimonial.image}
            />
          ))}
        </TestimonialCard.Group>
      </Page.Section>

      <SectionCTA
        className="to-plain"
        heading={
          (
            <span className="x-highlighted c-x">
              Ready to power up your superhuman team?
            </span>
          )
        }
        subtitle="Complete projects in 24 hours, starting at $20 per hour."
        buttonText="Get started"
      />

      <Page.Section
        id="profile-faq"
        className="x-background-far b-x"
        isSpaced
      >
        <h2 className="span-8 md-start-2 md-span-8">
          Common Questions
        </h2>
        <FAQ className="span-8 md-start-2 md-span-8 s1">
          {faqMap.map((item) => (
            <FAQ.Item question={item.question} titleAs="h3">
              <p>
                {item.answer}
              </p>
            </FAQ.Item>
          ))}
        </FAQ>
      </Page.Section>

      <Footer className="from-plain" />
    </ConversionPage>
  )
}

export default HowItWorks
